import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useNavigate } from "react-router-dom";
import { useAgency } from "@/contexts/AgencyContext";

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const { agency } = useAgency();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!email) {
      setError("Por favor, informe seu e-mail");
      return;
    }

    if (!agency) {
      setError("Agência não identificada");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/forgot-password`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email,
            domain: agency.domain,
          }),
        }
      );

      const result = await response.json();

      if (!response.ok || !result.success) {
        const errorMsg = result.error || "Erro ao enviar e-mail de recuperação";
        const details = result.details ? `\n\nDetalhes: ${result.details}` : "";
        setError(errorMsg + details);
        return;
      }

      setSuccess(true);
    } catch (err: any) {
      console.error("Erro ao solicitar recuperação:", err);
      setError("Erro de conexão. Tente novamente mais tarde.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="w-full h-full flex justify-center items-center relative">
        <img
          src="/login-gradient.png"
          className="absolute top-0 left-0 w-full h-full -z-10"
          alt=""
        />
        <div className="w-full gap-4 flex flex-col max-w-[425px] text-neutral-50">
          <div className="w-full gap-6 flex flex-col bg-neutral-600 rounded-md p-6">
            <img
              src={agency?.logo_url || "/logo-white.png"}
              className="h-[57px] max-w-[180px] self-center object-contain"
              alt={agency?.company_name || "Logo"}
              onError={(e) => {
                e.currentTarget.src = "/logo-white.png";
              }}
            />
            <div className="p-4 bg-green-500/20 border border-green-500/30 rounded-md">
              <p className="text-green-300 text-sm text-center font-semibold">
                Nova senha enviada por e-mail!
              </p>
              <p className="text-neutral-300 text-xs text-center mt-2">
                Verifique sua caixa de entrada e use a senha temporária para fazer login.
                Não esqueça de verificar a pasta de spam.
              </p>
              <p className="text-neutral-400 text-xs text-center mt-3">
                Recomendamos alterar esta senha após o primeiro login.
              </p>
            </div>
            <Button
              onClick={() => navigate("/login")}
              variant="primary"
              className="mt-4"
            >
              Voltar para o login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex justify-center items-center relative">
      <img
        src="/login-gradient.png"
        className="absolute top-0 left-0 w-full h-full -z-10"
        alt=""
      />
      <div className="w-full gap-4 flex flex-col max-w-[425px] text-neutral-50">
        <div className="w-full gap-6 flex flex-col bg-neutral-600 rounded-md p-6">
          <img
            src={agency?.logo_url || "/logo-white.png"}
            className="h-[57px] max-w-[180px] self-center object-contain"
            alt={agency?.company_name || "Logo"}
            onError={(e) => {
              e.currentTarget.src = "/logo-white.png";
            }}
          />
          <div>
            <h2 className="text-xl font-semibold mb-2">Esqueceu sua senha?</h2>
            <p className="text-sm text-neutral-300">
              Informe seu e-mail para receber um link de recuperação.
            </p>
          </div>
          {error && (
            <div className="p-3 bg-red-500/20 border border-red-500/30 rounded-md">
              <p className="text-red-300 text-sm whitespace-pre-line">{error}</p>
            </div>
          )}
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="form-group flex flex-col gap-2">
              <label htmlFor="email" className="text-sm">
                E-mail
              </label>
              <Input
                type="email"
                id="email"
                placeholder="seu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <Button type="submit" className="mt-4" disabled={loading}>
              {loading ? "Enviando..." : "Recuperar senha"}
            </Button>
            <button
              type="button"
              onClick={() => navigate("/login")}
              className="text-sm font-light text-neutral-300 hover:text-neutral-50 transition-colors"
            >
              Voltar para o login
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
